import React from 'react';
import Link from 'next/link';
import { DateTime, Duration } from 'luxon';
import camelcaseKeys from 'camelcase-keys';
import { Row, Col, Card, Pagination, Badge, Nav } from 'react-bootstrap';
import Prismic from 'prismic-javascript';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import OpenGraphTitle from '../../components/OpenGraphTitle';
import OpenGraphDescription from '../../components/OpenGraphDescription';
import Section from '../../components/Section';
import PageTitle from '../../components/PageTitle';
import SwooptyBg from '../../components/SwooptyBg';
import Paginator from '../../components/Paginator';
import { PrismicClient } from '../../SDKs/Prismic/prismic.config';
import { BlogItemDataCamelCase } from '../../SDKs/Prismic/prismic.types';
import { FranchiseeProvider } from '../../context/Franchisee';
import SVG from 'react-inlinesvg';
import Head from '../../components/head';

const iconPageNotFound = '/images/icon-404.svg';

export const getStaticProps = async (context) => {
  const { req } = context;

  const queryResult = await PrismicClient(req).query(
    Prismic.Predicates.at('document.type', 'blog-post'),
    { pageSize: 6, orderings: '[my.blog-post.dateposted desc]' }
  );

  const {
    page,
    prev_page: prevPage,
    next_page: nextPage,
    results_per_page: resultsPerPage,
    results_size: resultsSize,
    total_pages: totalPages,
    results,
  } = await queryResult;

  const blogItems = results
    .filter(({ uid, data }) => uid !== null && data.franchiseeslug === null)
    .map((item) => camelcaseKeys({ ...item, data: camelcaseKeys(item.data) }));

  const meta = {
    page,
    prevPage,
    nextPage,
    resultsPerPage,
    resultsSize,
    totalPages,
  };

  return {
    props: {
      meta,
      blogItems,
    },
    // revalidate: Duration.fromObject({ minutes: 15 }).as('seconds'),
    revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
  };
};

interface BlogPageProps {
  /* add blog page props here */
  blogItems: Array<{
    id: string;
    firstPublicationDate: string;
    lastPublicationDate: string;
    type: string;
    slugs: Array<string>;
    tags: Array<string>;
    data: BlogItemDataCamelCase;
    uid: string;
  }>;
  meta: {
    page: number;
    prevPage?: number;
    nextPage?: number;
    resulstsPerPage: number;
    resultsSize: number;
    totalPages: number;
  };
}

const Blog: React.FC<BlogPageProps> = ({ blogItems, meta }: BlogPageProps) => (
  <FranchiseeProvider>
    <Layout>
      <Head
        slug='blog'
        title='Blog - Two Maids - Professional Cleaning Services'
        description={`Tips, updates, stories from the field, and information on our professional cleaning services from our blogs.`}
      />
      <OpenGraphTitle title='Blog - Two Maids - Professional Cleaning Services ' />

      <OpenGraphDescription>
        Tips, updates, stories from the field, and information on our professional cleaning services from our blogs.
      </OpenGraphDescription>
      <SwooptyBg />
      <Section lg={12} md={12}>
        <PageTitle className=''>
          The Latest Blog Posts from <br />
          Two Maids 
        </PageTitle>

        <Row className='justify-content-center'>
          {meta.resultsSize ? (
            blogItems.map(({ data, id, firstPublicationDate, uid, tags }) => {
              const publishedDate = DateTime.fromISO(data.dateposted);
              const fallbackDate = DateTime.fromISO(firstPublicationDate);
              return (
                <Link href={`/blog/${uid}`}>
                  <Col
                    className='mb-4'
                    xs={12}
                    md={4}
                    style={{ textAlign: 'center' }}
                  >
                    <Card
                      className='blog-card'
                      key={id}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <Row>
                        {data.banner.url && (
                          <Col xs={12} md={12}>
                            {/* <img className="img-fluid rounded" src={data.banner.url} alt={data.banner.alt} /> */}
                            <Card.Img
                              variant='top'
                              src={data.banner.url}
                              alt={data.banner.alt}
                              style={{
                                width: '100%',
                                height: 230,
                                objectFit: 'cover',
                                overflow: 'hidden',
                              }}
                            />
                          </Col>
                        )}
                        <Col xs={12} md={12}>
                          <Card.Title
                            className='px-4 pt-4 ellipsis'
                            style={{
                              fontSize: '18px',
                              lineHeight: '26px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                              color: 'black',
                              marginBottom: 0,
                            }}
                          >
                            <span>{data.blogTitle[0]?.text}</span>
                          </Card.Title>
                        </Col>
                      </Row>
                      <hr
                        style={{
                          width: '100px',
                          margin: '16px auto',
                          backgroundColor: '#D4357F',
                        }}
                      />
                      <Row>
                        <Col xs={12} md={12} className='px-4'>
                          {data.authorName !== null && (
                            <>
                              {/* fontWeight needs to be medium and not throw an error */}
                              <span style={{ fontSize: 14, fontWeight: 500 }}>
                                By: {data.authorName}:
                              </span>
                              <br />
                            </>
                          )}
                
                        </Col>
                        {/* <Col xs={12} md={5} className="d-flex justify-content-end align-items-baseline">
                          <Link href={`/blog/${uid}`}>
                            <a className="btn btn-primary">Read More</a>
                          </Link>

                        </Col> */}
                      </Row>
                      <Row className='px-2 pb-4'>
                        <Col>
                          {tags.length > 0 && (
                            <>
                              {/* <span>
                              Filed Under:
                            </span> */}
                              <br />
                              {/* fontWeight needs to be medium and not throw an error */}
                              {tags.slice(0, 6).map(
                                (tag) =>
                                  tag.length < 14 && (
                                    <Badge
                                      key={tag}
                                      className='mr-2 px-2'
                                      style={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        background: '#D4357F',
                                        color: 'white',
                                      }}
                                    >
                                      {tag}
                                    </Badge>
                                  )
                              )}
                            </>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Link>
              );
            })
          ) : (
            <Col xs={10} lg={12} xl={10}>
              <SVG
                src={iconPageNotFound}
                title='A piece of paper'
                className='w-100 mb-4'
              />

              <h1 className='h2 text-center mb-5'>
                We&apos;re Sorry. Two Maids  hasn&apos;t posted any
                content yet.
              </h1>

              <p className='text-center'>
                Maybe one of these links will help you find what you&apos;re
                looking for.
              </p>
              <Nav className='d-block d-lg-flex justify-content-center my-5'>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/locations/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Find a Store <FontAwesomeIcon icon={faSearch} />
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/services/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Cleaning Packages
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/book/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Get a Quote
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          )}
        </Row>

        {meta.totalPages > 1 && (
          <Row className='justify-content-center'>
            <Col xs={12} md={8} className='d-flex justify-content-end'>
              <Paginator meta={meta} hrefBase='/blog/page/' />
            </Col>
          </Row>
        )}
      </Section>
    </Layout>
  </FranchiseeProvider>
);

// Blog.defaultProps = {
//   blogItems: [],
//   meta: ({
//     prevPage: null,
//     nextPage: null,
//     resulstsPerPage: 20,
//     resultsSize: 0,
//     totalPages: 1,
//   }),
// };

export default Blog;
